import { defineMessages } from 'react-intl'

export const ACHIEVEMENT_CLASS_NAME = 'Achievement'

export const ACHIEVEMENT_KEY = {
  ID: 'id',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  CLASS_NAME: 'className',

  TYPE: 'type',
  STATUS: 'status',
  LEVEL: 'level',
  COMPLETION_COUNT: 'completionCount',
  NEXT_LEVEL_COMPLETION_COUNT: 'nextLevelCompletionCount',

  // Foreign Keys
  PROFILE_ID: 'profileId'
}

// TODO:
// - cardio workouts
// - other workout types
// - created your own workouts
export const ACHIEVEMENT_TYPE = {
  FITNESS_DATA_COMPLETED: 1,
  WEEKLY_WORKOUTS_COUNT_GOAL_COMPLETED: 2,
  WORKOUT_COMPLETED: 3,
  PLAN_COMPLETED: 4,
  WORKOUT_SHARED: 5,
  ONE_REP_MAX_SET: 6,
  SMART_WORKOUT_COMPLETED: 7,
  SMART_PLAN_COMPLETED: 8,
  ADD_BEFORE_AFTER_PICTURE: 9,
  WEIGH_YOURSELF: 10,
  REFLECTION: 11,
  SHARE_WORKOUT_COMPLETED: 12,
  CALORIES_BURNED: 13,
  TOTAL_TRAINING_TIME_IN_HOURS: 14,
  WORKOUTS_COMPLETED_IN_A_ROW: 15
}

const DEFAULT_VALUES = [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 21000, 42000]
const ACHIEVEMENT_TYPE_TO_VALUE_MULTIPLIER = {
  [ACHIEVEMENT_TYPE.CALORIES_BURNED]: 500,
  [ACHIEVEMENT_TYPE.ONE_REP_MAX_SET]: 0.8,
  [ACHIEVEMENT_TYPE.TOTAL_TRAINING_TIME_IN_HOURS]: 5,
  [ACHIEVEMENT_TYPE.WORKOUTS_COMPLETED_IN_A_ROW]: 3
}

export const ACHIEVEMENT_TYPE_TO_LEVEL_THRESHOLDS = Object
  .values(ACHIEVEMENT_TYPE)
  .reduce((obj, key) => {
    const valueMultiplier = ACHIEVEMENT_TYPE_TO_VALUE_MULTIPLIER[key] || 1
    obj[key] = DEFAULT_VALUES.map(val => {
      if (valueMultiplier < 1 && val === 1) {
        return val
      }
      return val * valueMultiplier
    })
    return obj
  }, {})

export const achievementMessage = defineMessages({
  fitnessDataFilled: {
    id: 'achievement.fitnessDataFilled',
    defaultMessage: 'Fitness Data Filled'
  },
  weeklyWorkoutGoalCompleted: {
    id: 'achievement.weeklyWorkoutGoalCompleted',
    defaultMessage: 'Brick By Brick'
  },
  workoutCompleted: {
    id: 'achievement.workoutCompleted',
    defaultMessage: 'Workout Master'
  },
  planCompleted: {
    id: 'achievement.planCompleted',
    defaultMessage: 'The Achiever'
  },
  workoutShared: {
    id: 'achievement.workoutShared',
    defaultMessage: 'Share The Love'
  },
  oneRepMaxSet: {
    id: 'achievement.oneRepMaxSet',
    defaultMessage: 'Another One'
  },
  smartWorkoutCompleted: {
    id: 'achievement.smartWorkoutCompleted',
    defaultMessage: 'Work Smart'
  },
  smartPlanCompleted: {
    id: 'achievement.smartPlanCompleted',
    defaultMessage: 'I Adapt'
  },
  addBeforeAfterPicture: {
    id: 'achievement.addBeforeAfterPicture',
    defaultMessage: 'How Far I’ve Come'
  },
  weighYourself: {
    id: 'achievement.weighYourself',
    defaultMessage: 'Trusting The Data'
  },
  reflection: {
    id: 'achievement.reflection',
    defaultMessage: 'Creating My Success Story'
  },
  shareWorkoutCompleted: {
    id: 'achievement.shareWorkoutCompleted',
    defaultMessage: 'Sharing My Progress'
  },
  caloriesBurned: {
    id: 'achievement.caloriesBurned',
    defaultMessage: 'Burn It{br}All'
  },
  totalTrainingTimeInHours: {
    id: 'achievement.totalTrainingTimeInHours',
    defaultMessage: 'Make The Time Count'
  },
  workoutsCompletedInARow: {
    id: 'achievement.workoutsCompletedInARow',
    defaultMessage: 'Streak Sensei'
  },

  fitnessDataFilledDescription: {
    id: 'achievement.fitnessDataFilledDescription',
    defaultMessage: 'Making your fitness data more accurate will help us provide you better advice to help you achieve your goals.'
  },
  weeklyWorkoutsCountGoalSetDescription: {
    id: 'achievement.weeklyWorkoutsCountGoalSetDescription',
    defaultMessage: 'Great job on completing your weekly workout goals {completionCount} times. It will help you stay on track. Remember to aim for consistency, not perfection.'
  },
  workoutCompletedDescription: {
    id: 'achievement.workoutCompletedDescription',
    defaultMessage: 'You completed {completionCount} workouts with Gymaholic. Keep up the hard work!'
  },
  planCompletedDescription: {
    id: 'achievement.planCompletedDescription',
    defaultMessage: 'A goal without a plan is just a dream. And we can tell that you’ve been busy living your dream by completing {completionCount} plans with us.'
  },
  workoutSharedDescription: {
    id: 'achievement.workoutSharedDescription',
    defaultMessage: 'Let’s get better together. Keep sharing your workouts, it will others achieve their goals.'
  },
  oneRepMaxSetDescription: {
    id: 'achievement.oneRepMaxSetDescription',
    defaultMessage: 'Never not progression. Your one rep max is a great way to track your progress in your strength journey.'
  },
  smartWorkoutCompletedDescription: {
    id: 'achievement.smartWorkoutCompletedDescription',
    defaultMessage: 'Keep training smart, and switch things up to maximize results.'
  },
  smartPlanCompletedDescription: {
    id: 'achievement.smartPlanCompletedDescription',
    defaultMessage: 'Having a clear and sustainable plan will lead you to your goal, and you completed {completionCount} smart plans with us. Be proud of yourself! But keep moving forward.'
  },
  addBeforeAfterPictureDescription: {
    id: 'achievement.addBeforeAfterPictureDescription',
    defaultMessage: 'Before and after pictures are great way to see your improvements and your weaknesses. The ultimate goal is to feel good in your own skin, so keep tracking your progress!'
  },
  weighYourselfDescription: {
    id: 'achievement.weighYourselfDescription',
    defaultMessage: 'The scale never lies... we both know it does from time to time. But it’s a consistent way to track your eating habits and adjust them on a daily basis.'
  },
  reflectionDescription: {
    id: 'achievement.reflectionDescription',
    defaultMessage: 'As human beings we tend to overthink our “failures” and ignore our wins. Reflection is the best way to appreciate those successes, and turn those failures into lessons. Always a work in progress.'
  },
  shareWorkoutCompletedDescription: {
    id: 'achievement.shareWorkoutCompletedDescription',
    defaultMessage: 'Keep sharing your completed workouts, it will hold yourself accountable, and you’ll surprised how many people will support your growth.'
  },
  caloriesBurnedDescription: {
    id: 'achievement.caloriesBurnedDescription',
    defaultMessage: 'Think about how many bad nutrition choices you’ve burned with those {completionCount} kcals, it’s crazy how we can transform our bodies by putting in the work.'
  },
  totalTrainingTimeInHoursDescription: {
    id: 'achievement.totalTrainingTimeInHoursDescription',
    defaultMessage: 'You’ve spent {completionCount} hours improving yourself, no wonder you’re getting better!'
  },
  workoutsCompletedInARowDescription: {
    id: 'achievement.workoutsCompletedInARowDescription',
    defaultMessage: 'You’ve completed {completionCount} workouts in a row. It does not end here; keep moving, challenging yourself, and most importantly, take your active recovery days seriously.'
  }
})

export const ACHIEVEMENT_TYPE_TO_TITLE_MESSAGE = {
  [ACHIEVEMENT_TYPE.FITNESS_DATA_COMPLETED]: achievementMessage.fitnessDataFilled,
  [ACHIEVEMENT_TYPE.WEEKLY_WORKOUTS_COUNT_GOAL_COMPLETED]: achievementMessage.weeklyWorkoutGoalCompleted,
  [ACHIEVEMENT_TYPE.WORKOUT_COMPLETED]: achievementMessage.workoutCompleted,
  [ACHIEVEMENT_TYPE.PLAN_COMPLETED]: achievementMessage.planCompleted,
  [ACHIEVEMENT_TYPE.WORKOUT_SHARED]: achievementMessage.workoutShared,
  [ACHIEVEMENT_TYPE.ONE_REP_MAX_SET]: achievementMessage.oneRepMaxSet,
  [ACHIEVEMENT_TYPE.SMART_WORKOUT_COMPLETED]: achievementMessage.smartWorkoutCompleted,
  [ACHIEVEMENT_TYPE.SMART_PLAN_COMPLETED]: achievementMessage.smartPlanCompleted,
  [ACHIEVEMENT_TYPE.ADD_BEFORE_AFTER_PICTURE]: achievementMessage.addBeforeAfterPicture,
  [ACHIEVEMENT_TYPE.WEIGH_YOURSELF]: achievementMessage.weighYourself,
  [ACHIEVEMENT_TYPE.REFLECTION]: achievementMessage.reflection,
  [ACHIEVEMENT_TYPE.SHARE_WORKOUT_COMPLETED]: achievementMessage.shareWorkoutCompleted,
  [ACHIEVEMENT_TYPE.CALORIES_BURNED]: achievementMessage.caloriesBurned,
  [ACHIEVEMENT_TYPE.TOTAL_TRAINING_TIME_IN_HOURS]: achievementMessage.totalTrainingTimeInHours
}

export const ACHIEVEMENT_TYPE_TO_DESCRIPTION_MESSAGE = {
  [ACHIEVEMENT_TYPE.FITNESS_DATA_COMPLETED]: achievementMessage.fitnessDataFilledDescription,
  [ACHIEVEMENT_TYPE.WEEKLY_WORKOUTS_COUNT_GOAL_COMPLETED]: achievementMessage.weeklyWorkoutsCountGoalSetDescription,
  [ACHIEVEMENT_TYPE.WORKOUT_COMPLETED]: achievementMessage.workoutCompletedDescription,
  [ACHIEVEMENT_TYPE.PLAN_COMPLETED]: achievementMessage.planCompletedDescription,
  [ACHIEVEMENT_TYPE.WORKOUT_SHARED]: achievementMessage.workoutSharedDescription,
  [ACHIEVEMENT_TYPE.ONE_REP_MAX_SET]: achievementMessage.oneRepMaxSetDescription,
  [ACHIEVEMENT_TYPE.SMART_WORKOUT_COMPLETED]: achievementMessage.smartWorkoutCompletedDescription,
  [ACHIEVEMENT_TYPE.SMART_PLAN_COMPLETED]: achievementMessage.smartPlanCompletedDescription,
  [ACHIEVEMENT_TYPE.ADD_BEFORE_AFTER_PICTURE]: achievementMessage.addBeforeAfterPictureDescription,
  [ACHIEVEMENT_TYPE.WEIGH_YOURSELF]: achievementMessage.weighYourselfDescription,
  [ACHIEVEMENT_TYPE.REFLECTION]: achievementMessage.reflectionDescription,
  [ACHIEVEMENT_TYPE.SHARE_WORKOUT_COMPLETED]: achievementMessage.shareWorkoutCompletedDescription,
  [ACHIEVEMENT_TYPE.CALORIES_BURNED]: achievementMessage.caloriesBurnedDescription,
  [ACHIEVEMENT_TYPE.TOTAL_TRAINING_TIME_IN_HOURS]: achievementMessage.totalTrainingTimeInHoursDescription
}

export const ACHIEVEMENT_AUDIT_CLASS_NAME = 'AchievementAudit'

export const ACHIEVEMENT_ORDER = [
  [
    'id',
    'DESC'
  ]
]

export const ACHIEVEMENT_STATUS = {
  COMPLETED: 1
}

export const ACHIEVEMENT_PRIVATE_KEYS = []

export const ACHIEVEMENT_FOREIGN_KEYS = [
  ACHIEVEMENT_KEY.PROFILE_ID
]

export const ACHIEVEMENT_AUDIT = {
  CREATED: 'Achievement Created',
  UPDATED: 'Achievement Updated'
}

export const ACHIEVEMENT_SCOPE = {
  defaultScope: {
    attributes: {
      exclude: ACHIEVEMENT_PRIVATE_KEYS
    }
  }
}
